// ---- Shared archetype gait ----
// A single per-archetype animator that poses the limb groups the archetype
// builders publish (via meshkit's `limb()` — legs/arms/wings tagged
// `userData.limb`). It gives every quadruped / humanoid / bird / bug a basic
// WALK, a bigger RUN, and a one-shot ATTACK with no per-model work: rig the
// anatomy once, animate the whole family.
//
// Driven entirely by flags on the object's userData, so it's a drop-in for the
// studio (models view sets them from the Gait/Attack panel) AND reusable in-game
// later (the server/net layer can set the same flags per NPC):
//   studioMoving   — walking (gait blends in)
//   studioRunning  — running (bigger throws + lean; only while moving)
//   studioAttack   — set truthy to TRIGGER one attack (consumed → an attack clock)
//   studioBaseY    — feet-on-floor offset stashed by catalog.normalize()
// It never touches shared materials and only writes limb-group + root transforms,
// so it composes with the detail-parts layer (combs/eyes ride the root).
//
// THREE-only + DOM-free — same portability contract as the archetype builders.

import * as THREE from "three";
import { animateFaceMouth } from "./face.ts";

export type GaitArchetype = "quadruped" | "humanoid" | "bird" | "bug";

/** Inferred-locomotion thresholds (world units/sec, from a mover's transform
 *  delta): below WALK it's idle; above RUN it plays the run cycle — set so a
 *  chase (pursueSpeed ~2.2) walks and a wildlife bolt (fleeSpeed ~8.4) runs.
 *  Shared by the game's NPC driver (npc/index.ts driveGait) and the studio's
 *  combat sandbox so the classification can't drift. */
export const GAIT_WALK_SPEED = 0.4;
export const GAIT_RUN_SPEED = 4.5;

/** Seconds one attack plays out over. Exported for headless scrub
 *  (`snapGaitAttack` / `inspect-model --pose=attack` on critters). */
export const GAIT_ATTACK_DUR = 0.55;
const ATTACK_DUR = GAIT_ATTACK_DUR;

type GaitState = {
  walkBlend: number; // 0 idle → 1 walking (eased)
  runBlend: number;  // 0 walk → 1 run (eased)
  phase: number;     // stride phase (advances only while moving)
  attackT: number;   // seconds left in the current attack (0 = none)
  limbs?: Record<string, THREE.Group>;
  /** Rest positions captured when limbs are collected — positional gait writes
   *  (shoulder shift, torso breath) are rest+delta, so a pivot that lives at a
   *  non-zero height (e.g. the realistic humanoid's torso at hip height) isn't
   *  teleported by an absolute write. */
  rests?: Record<string, { x: number; y: number; z: number }>;
  /** Whether the head / shoulder pivots NEST INSIDE the torso pivot (the
   *  realistic humanoid) vs sit beside it (the player rig). Nested pivots
   *  already ride the torso's run-lean, so the gait must not ALSO shove them
   *  forward to chase it — doing both detaches the head from the neck. */
  chained?: { head: boolean; arms: boolean };
};

function state(o: THREE.Object3D): GaitState {
  let s = o.userData.__gait as GaitState | undefined;
  if (!s) {
    s = { walkBlend: 0, runBlend: 0, phase: 0, attackT: 0 };
    o.userData.__gait = s;
  }
  return s;
}

/** Drop the cached limb map so the next frame re-collects it. MUST be called
 *  whenever a rig's children are swapped out (e.g. redressPlayer rebuilds the
 *  body) — otherwise the animator keeps writing onto the OLD, detached joint
 *  groups and the visible new body freezes mid-stride. Phase/blends are kept so
 *  there's no gait pop. */
export function invalidateGaitLimbs(o: THREE.Object3D): void {
  const s = o.userData.__gait as GaitState | undefined;
  if (s) { s.limbs = undefined; s.rests = undefined; s.chained = undefined; }
}

/** Collect (once) every limb group the builder tagged, keyed by name. */
function limbsOf(o: THREE.Object3D, s: GaitState): Record<string, THREE.Group> {
  if (s.limbs) return s.limbs;
  const map: Record<string, THREE.Group> = {};
  const rests: Record<string, { x: number; y: number; z: number }> = {};
  o.traverse((c) => {
    const n = c.userData?.limb as string | undefined;
    if (n) {
      map[n] = c as THREE.Group;
      rests[n] = { x: c.position.x, y: c.position.y, z: c.position.z };
    }
  });
  s.limbs = map;
  s.rests = rests;
  const under = (child: THREE.Object3D | undefined, root: THREE.Object3D | undefined) => {
    if (!child || !root) return false;
    for (let p = child.parent; p; p = p.parent) if (p === root) return true;
    return false;
  };
  s.chained = { head: under(map.head, map.torso), arms: under(map.shoulderL, map.torso) };
  return map;
}

const approach = (v: number, target: number, rate: number) =>
  v + (target - v) * Math.min(1, rate);

/** Humanoid gait amplitudes — the knobs of the (ported) player walk/run cycle.
 *  Set `userData.gaitProfile` (partial, merged over NPC_PROFILE) to retune one
 *  rig; the player passes its historical values verbatim (PLAYER_PROFILE) so
 *  its runtime look is unchanged by the unification. */
export type GaitProfile = {
  cadence: number;    // stride phase speed while walking
  cadenceRun: number; // extra phase speed at full run
  legAmp: number;     // hip swing
  legAmpRun: number;
  armAmp: number;     // shoulder counter-swing
  armAmpRun: number;
  kneeAmp: number;    // knee tuck on the back-swing
  kneeAmpRun: number;
  bob: number;        // vertical bounce per footfall
  bobRun: number;
};

/** The player rig's historical gait values (player.ts pre-unification). */
export const PLAYER_PROFILE: GaitProfile = {
  cadence: 9, cadenceRun: 7,
  legAmp: 0.85, legAmpRun: 0.45,
  armAmp: 0.6, armAmpRun: 0.6,
  kneeAmp: 1.3, kneeAmpRun: 0.6,
  bob: 0.14, bobRun: 0.1,
};

/** Default for humanoid NPCs — a touch slower and smaller than the hero. */
export const NPC_PROFILE: GaitProfile = {
  cadence: 7, cadenceRun: 6,
  legAmp: 0.7, legAmpRun: 0.45,
  armAmp: 0.5, armAmpRun: 0.5,
  kneeAmp: 1.1, kneeAmpRun: 0.5,
  bob: 0.05, bobRun: 0.05,
};

/** Per-call options. */
export type GaitOpts = {
  /** Write root transforms (body bob on `position.y` + lunge/lean on `rotation.x`).
   *  Default true (studio: the animator fully owns the model). Pass false for
   *  networked in-game NPCs, where net.ts owns `position` and combat.ts owns
   *  `rotation.x` (attack lunge / flee pitch) + `rotation.z` (topple) — the gait
   *  then only poses the LIMBS, layering onto those root reactions. */
  root?: boolean;
};

/** Animate one archetype instance for a frame. `o` is the built + normalized
 *  group; gait/attack are read from its userData (see the module header).
 *  Returns the vertical bob offset for this frame — written to `position.y`
 *  when the animator owns the root, and returned either way so a root-owning
 *  caller (the player's ground-height resolve in game.ts) can apply it. */
export function animateGait(
  arch: GaitArchetype,
  o: THREE.Object3D,
  dt: number,
  time: number,
  opts?: GaitOpts,
): number {
  const g = o as THREE.Group;
  const s = state(o);
  const L = limbsOf(o, s);

  const moving = !!g.userData.studioMoving;
  const running = !!g.userData.studioRunning;
  // Only humanoids read the profile, and only rigs that SET one (the player)
  // need the merge — don't allocate per frame for every critter NPC.
  const over = arch === "humanoid" ? (g.userData.gaitProfile as Partial<GaitProfile> | undefined) : undefined;
  const prof: GaitProfile = over ? { ...NPC_PROFILE, ...over } : NPC_PROFILE;

  // Ease idle↔walk and walk↔run so toggles glide.
  s.walkBlend = approach(s.walkBlend, moving ? 1 : 0, dt * 10);
  s.runBlend = approach(s.runBlend, moving && running ? 1 : 0, dt * 8);
  const blend = s.walkBlend;
  const run = s.runBlend;

  // Stride cadence — humanoids use their profile; critters keep the family rate.
  // Per-instance seeded jitter (userData.gaitJitter, set by the dressing layer)
  // desynchronizes a crowd: a phase offset + a cadence multiplier.
  const jit = g.userData.gaitJitter as { phase?: number; speed?: number } | undefined;
  let cadence = arch === "humanoid" ? prof.cadence + run * prof.cadenceRun : 7 + run * 6;
  if (jit?.speed) cadence *= 1 + jit.speed;
  if (moving) s.phase += dt * cadence;
  const ph = s.phase + (jit?.phase ?? 0);
  const swing = Math.sin(ph);

  // Attack clock: consume the one-shot trigger, then count down.
  if (g.userData.studioAttack) {
    s.attackT = ATTACK_DUR;
    g.userData.studioAttack = false;
  }
  s.attackT = Math.max(0, s.attackT - dt);
  // 0 → 1 → 0 bell over the attack window.
  const atk = s.attackT > 0 ? Math.sin((1 - s.attackT / ATTACK_DUR) * Math.PI) : 0;

  const rx = (name: string, v: number) => { const l = L[name]; if (l) l.rotation.x = v; };
  const ry = (name: string, v: number) => { const l = L[name]; if (l) l.rotation.y = v; };
  const rz = (name: string, v: number) => { const l = L[name]; if (l) l.rotation.z = v; };
  // Positional writes are rest+delta (see GaitState.rests).
  const py = (name: string, d: number) => { const l = L[name]; if (l) l.position.y = (s.rests?.[name]?.y ?? 0) + d; };
  const pz = (name: string, d: number) => { const l = L[name]; if (l) l.position.z = (s.rests?.[name]?.z ?? 0) + d; };

  let rootPitch = 0;
  let bob = 0;

  switch (arch) {
    case "quadruped": {
      // Jointed build (legJoints — knee/hock pivots present): the expressive
      // horse-family gait. A 4-beat lateral-sequence WALK (LH→LF→RH→RF, each a
      // quarter-cycle apart) morphs into a transverse GALLOP (hinds near-synced,
      // fronts near-synced, half a cycle apart) as the run blend rises; the
      // lower leg folds through each swing phase, the neck nods with the
      // stride and stretches into the run, the tail streams. Rigs without knee
      // limbs fall through to the classic one-piece diagonal gait below.
      if (L.kneeFL) {
        const lerpN = (a: number, b: number) => a + (b - a) * run;
        // Per-leg stride offsets, walk → gallop (radians).
        const OFF: Record<string, number> = {
          legBL: 0,
          legFL: lerpN(Math.PI * 0.5, Math.PI * 1.0),
          legBR: lerpN(Math.PI * 1.0, Math.PI * 0.3),
          legFR: lerpN(Math.PI * 1.5, Math.PI * 1.3),
        };
        const amp = (0.42 + run * 0.38) * blend;
        const kneeAmp = (0.55 + run * 0.8) * blend;
        for (const leg of ["legFL", "legFR", "legBL", "legBR"]) {
          const p = ph + OFF[leg]!;
          rx(leg, Math.sin(p) * amp);
          // Fold the cannon mid-swing (protraction = hip rx falling, cos < 0);
          // hocks tuck a touch less than knees so front/rear read differently.
          const fold = Math.max(0, -Math.cos(p));
          rx(`knee${leg.slice(3)}`, fold * kneeAmp * (leg[3] === "B" ? 0.8 : 1));
        }
        // Suspension + gallop rock: the body pitches over the stride and the
        // bounce deepens as the hinds gather under it.
        rootPitch += Math.sin(ph + 0.6) * 0.07 * run * blend;
        bob = (Math.abs(swing) * 0.03 + Math.max(0, Math.sin(ph + 1.1)) * 0.09 * run) * blend;
        // Neck: rhythmic nod each stride at the walk, stretched low + forward
        // at the gallop; idle = a slow look-around + grazing dips.
        const idleQ = 1 - blend;
        rx("neck",
          Math.sin(ph + 0.9) * (0.11 - run * 0.05) * blend
          + run * 0.30 * blend
          + Math.max(0, Math.sin(time * 0.35)) * 0.35 * idleQ);
        ry("neck", Math.sin(time * 0.5) * 0.14 * idleQ);
        // Tail: lazy swish at rest, sways with the walk, lifts + streams at run.
        rx("tail", run * 0.55 * blend + Math.sin(ph * 2 + 1.7) * 0.06 * blend);
        rz("tail", Math.sin(time * 1.6) * 0.10 * (idleQ + 0.3) + Math.sin(ph + 0.4) * 0.12 * blend);
        // Attack: a coil → pounce-bite. Weight gathers onto the folded hinds,
        // then the body springs into a forward lunge while the neck strikes and
        // the forepaws whip forward with folded knees (the wolf's snap; a prey
        // kind never fires it — flee overrides the chase AI).
        if (atk > 0.001) {
          const u = 1 - s.attackT / ATTACK_DUR; // 0 start → 1 end
          const coil = Math.sin(Math.min(1, u / 0.3) * Math.PI) * (u < 0.38 ? 1 : 0);
          const drive = Math.sin(Math.min(1, Math.max(0, (u - 0.18) / 0.5) * Math.PI));
          // Forelegs reach past the chest on the spring, knees folding through
          // the strike then landing; hinds drive back under the lunge.
          rx("legFL", Math.sin(ph + OFF.legFL!) * amp * (1 - atk) + coil * 0.5 - drive * 1.0);
          rx("legFR", Math.sin(ph + OFF.legFR!) * amp * (1 - atk) + coil * 0.5 - drive * 1.1);
          rx("kneeFL", coil * 1.2 + drive * 0.5);
          rx("kneeFR", coil * 1.2 + drive * 0.6);
          rx("legBL", Math.sin(ph + OFF.legBL!) * amp * (1 - atk) + coil * 0.35 + drive * 0.55);
          rx("legBR", Math.sin(ph + OFF.legBR!) * amp * (1 - atk) + coil * 0.3 + drive * 0.5);
          rx("kneeBL", coil * 0.9); rx("kneeBR", coil * 0.9);
          // Head chambers back on the coil, then snaps forward-down = the bite.
          rx("neck", -coil * 0.22 + drive * 0.55);
          // Tail whips up through the spring.
          rx("tail", coil * 0.15 + drive * 0.45);
          // Body: crouch → forward pitch into the lunge.
          rootPitch += -coil * 0.18 + drive * 0.5;
          bob += coil * 0.055 - drive * 0.02;
        }
        break;
      }
      // Diagonal gait: FL+BR together, FR+BL opposite.
      const amp = (0.5 + run * 0.4) * blend;
      rx("legFL", swing * amp); rx("legBR", swing * amp);
      rx("legFR", -swing * amp); rx("legBL", -swing * amp);
      bob = Math.abs(swing) * (0.03 + run * 0.03) * blend;
      // Attack: body lunge + a front-paw swipe (not just a head-butt pitch).
      // Progress 0→1 across ATTACK_DUR; `atk` is the 0→1→0 envelope so recover
      // eases out. Phase the strike paw: chamber (rear/up) → swipe (forward/down).
      if (atk > 0.001) {
        const u = 1 - s.attackT / ATTACK_DUR; // 0 start → 1 end
        // Coil early (weight back), drive mid-window, settle late.
        const coil = Math.sin(Math.min(1, u / 0.3) * Math.PI) * (u < 0.38 ? 1 : 0);
        const drive = Math.sin(Math.min(1, Math.max(0, (u - 0.18) / 0.5) * Math.PI));
        // Strike paw (front +x = legFR): chamber aft/up, then whip forward/down.
        // Positive rx swings the foot aft; negative rx reaches forward (+z face).
        const chamber = Math.sin(Math.min(1, u / 0.38) * Math.PI); // 0→1→0 early lift
        const swipe = Math.sin(Math.min(1, Math.max(0, (u - 0.28) / 0.45) * Math.PI)); // mid peak
        const gaitFL = swing * amp;
        const gaitFR = -swing * amp;
        const gaitBL = -swing * amp;
        const gaitBR = swing * amp;
        // Overwrite gait on the attacking limbs so the swipe reads cleanly.
        // Chamber keeps the paw high (big +rx); swipe snaps it past the chest.
        rx("legFR", gaitFR * (1 - atk) + chamber * 1.25 - swipe * 1.15);
        // Lateral open on the strike so it reads as a claw swipe, not a pure step.
        rz("legFR", chamber * 0.35 - swipe * 0.7);
        // Support front plants forward; rears push the body into the lunge.
        rx("legFL", gaitFL * (1 - atk) - drive * 0.6 - coil * 0.1);
        rx("legBL", gaitBL * (1 - atk) + drive * 0.45 + coil * 0.25);
        rx("legBR", gaitBR * (1 - atk) + drive * 0.4 + coil * 0.2);
        // Body: crouch coil → forward pitch on the strike (the "lunge").
        rootPitch += -coil * 0.16 + drive * 0.52 + atk * 0.06;
        bob += coil * 0.05 - drive * 0.025;
      } else {
        // Clear attack-only lateral on the strike paw so it doesn't stick after.
        rz("legFR", 0);
      }
      break;
    }
    case "humanoid": {
      // THE humanoid walk/run — the player rig's gait (ported verbatim from
      // player.ts at unification), scaled by the rig's GaitProfile. Rig-contract
      // joint names (src/model/rig.ts); every write no-ops on rigs missing the
      // joint (simple humanoids lack knees/torso/head pivots).
      const legAmp = (prof.legAmp + run * prof.legAmpRun) * blend;
      const armAmp = (prof.armAmp + run * prof.armAmpRun) * blend;

      // Hips swing fore/aft, opposite legs. Knees only ever fold BACKWARD
      // (heel toward butt, +x here), peaking just after toe-off — so the
      // trailing heel kicks up while the reaching leg lands near-straight.
      rx("hipL", swing * legAmp); rx("hipR", -swing * legAmp);
      const kneeAmp = (prof.kneeAmp + run * prof.kneeAmpRun) * blend;
      const tuck = run * 0.25 * blend; // constant crouch-tuck at full run
      const kick = Math.sin(ph - 0.55); // hip-back phase, delayed past toe-off
      rx("kneeL", Math.max(0, kick) * kneeAmp + tuck);
      rx("kneeR", Math.max(0, -kick) * kneeAmp + tuck);

      // Arms counter-swing; elbows pump harder as the run sets in. The attack
      // overlay (one-shot studioAttack — NPC punches; the player's clip system
      // supersedes this and never sets the flag) throws the R arm forward.
      rx("shoulderL", -swing * armAmp + atk * 0.5);
      rx("shoulderR", swing * armAmp - atk * 1.7);
      // Same sibling-vs-nested rule as the head: only chase the run-lean on
      // rigs whose shoulders DON'T hang off the torso pivot.
      if (!s.chained?.arms) {
        pz("shoulderL", run * 0.28 * blend);
        pz("shoulderR", run * 0.28 * blend);
      }
      const elbowBase = 0.25 + run * 0.6;
      rx("elbowL", -(elbowBase + Math.max(0, -swing) * (0.35 + run * 0.4)) * blend * (1 - atk));
      rx("elbowR", -(elbowBase + Math.max(0, swing) * (0.35 + run * 0.4)) * blend * (1 - atk) - atk * 0.08);

      // Torso: forward lean into the run + a bounce per footfall + roll/twist;
      // the attack twists it behind the punch. Head keeps facing travel.
      const lean = (0.08 + run * 0.12 + Math.sin(ph * 2) * (0.015 + run * 0.02)) * blend;
      rx("torso", lean);
      rz("torso", Math.sin(ph) * (0.02 + run * 0.02) * blend);
      ry("torso", Math.sin(ph) * (0.025 + run * 0.03) * blend - atk * 0.3);
      // Head pitch is a WORLD target: a sibling head (player rig) takes it
      // raw + a forward shift to chase the leaning chest; a nested head
      // (realistic frame) inherits the lean, so counter-rotate it out instead
      // and skip the shift — the neck carries the head already.
      const headPitch = (run * 0.12 + Math.sin(ph * 2 + 0.6) * 0.025) * blend;
      rx("head", s.chained?.head ? headPitch - lean : headPitch);
      rz("head", -Math.sin(ph) * 0.018 * blend);
      if (!s.chained?.head) pz("head", run * 0.3 * blend);

      // Idle breathing + gentle look-around when not walking.
      const idle = 1 - blend;
      if (idle > 0.001) {
        py("torso", Math.sin(time * 2) * 0.03 * idle);
        ry("head", Math.sin(time * 0.5) * 0.15 * idle + atk * 0.22);
        rz("shoulderL", 0.04 * idle);
        rz("shoulderR", -0.04 * idle);
      } else {
        py("torso", 0);
        ry("head", atk * 0.22);
        rz("shoulderL", 0);
        rz("shoulderR", 0);
      }

      bob = Math.abs(swing) * (prof.bob + run * prof.bobRun) * blend;
      rootPitch += atk * 0.12; // small forward hitch into the punch

      // Talking face: the "talking" expression emits a limb("mouth") (face.ts);
      // oscillate it open/closed. Every other expression has no "mouth" limb, so
      // this no-ops — same limb map + invalidation the gait already maintains.
      const mouth = L.mouth;
      if (mouth) animateFaceMouth(mouth, time);
      break;
    }
    case "bird": {
      // Step the legs; a double-frequency root nod reads as the classic head-bob.
      const amp = (0.55 + run * 0.35) * blend;
      rx("legL", swing * amp); rx("legR", -swing * amp);
      // Idle wing settle → a small walk flutter.
      const flutter = Math.sin(ph * 2) * 0.08 * blend;
      rz("wingL", flutter); rz("wingR", -flutter);
      bob = Math.abs(swing) * (0.02 + run * 0.02) * blend;
      rootPitch += Math.sin(ph * 2) * 0.05 * blend;
      // Attack: a sharp forward peck + wings flared back.
      rootPitch += atk * 0.5;
      rz("wingL", flutter + atk * 0.7); rz("wingR", -flutter - atk * 0.7);
      break;
    }
    case "bug": {
      // Alternating-tripod scuttle: each leg offset by its index + its side.
      const amp = (0.3 + run * 0.25) * blend;
      const lift = (0.18 + run * 0.12) * blend;
      for (const name in L) {
        const m = /^leg([LR])(\d+)$/.exec(name);
        if (!m) continue;
        const side = m[1] === "L" ? 0 : Math.PI;
        const i = Number(m[2]);
        const p = ph + side + i * Math.PI; // neighbours + opposite sides in antiphase
        rx(name, Math.sin(p) * amp);
        rz(name, Math.max(0, Math.sin(p)) * lift * (m[1] === "L" ? 1 : -1));
      }
      bob = Math.abs(swing) * 0.015 * blend;
      // Attack: a low forward lunge + the front pair of legs reared up.
      rootPitch += atk * 0.22;
      if (atk > 0) { rx("legL0", -atk * 0.9); rx("legR0", -atk * 0.9); }
      break;
    }
  }

  if (opts?.root !== false) {
    g.rotation.x = rootPitch;
    g.position.y = ((g.userData.studioBaseY as number) ?? 0) + bob;
  }
  return bob;
}

/**
 * Freeze a shared archetype attack at normalized time `t` ∈ [0,1]
 * (0 ≈ wind-up start, ~0.5 peak, 1 = fully recovered). For headless scrub /
 * `inspect-model --pose=attack` on quadruped/bird/bug (studio/direct use too).
 * Clears prior gait attack state on `o`.
 *
 * Note: `t=0` samples early wind-up (not pure rest) so chamber limbs clear
 * the `atk > 0.001` gate; `t=1` is fully recovered.
 */
export function snapGaitAttack(
  arch: GaitArchetype,
  o: THREE.Object3D,
  t: number,
  opts?: GaitOpts,
): number {
  const s = state(o);
  s.attackT = 0;
  s.walkBlend = 0;
  s.runBlend = 0;
  o.userData.studioMoving = false;
  o.userData.studioRunning = false;
  o.userData.studioAttack = true;
  const u = Math.min(1, Math.max(0, t));
  // First frame arms the clock then subtracts dt; dt = progress·DUR lands ≈ progress.
  // Floor tiny so t=0 is wind-up start (quad attack needs atk > 0.001).
  const progress = u <= 0 ? 0.04 : u;
  const dt = progress * ATTACK_DUR;
  return animateGait(arch, o, dt, 0, opts);
}

/** Fixed sample times for gait-attack `--t=keys` (no clip keyframes).
 *  0 = wind-up, mid keys = strike, 1 = recovered rest. */
export const GAIT_ATTACK_KEYS = [0, 0.2, 0.35, 0.5, 0.7, 1] as const;

/**
 * Freeze a shared archetype WALK (`running` false) or RUN cycle at stride phase
 * `t` ∈ [0,1] (one full stride = 2π). Blends are forced to full weight so a
 * single frame shows the true mid-stride pose — the locomotion twin of
 * `snapGaitAttack`, for headless scrub (`inspect-model --pose=walk|run`):
 * verify leg swing/knee fold/neck nod without driving the app. Clears any
 * pending attack on `o`.
 */
export function snapGaitStride(
  arch: GaitArchetype,
  o: THREE.Object3D,
  t: number,
  running: boolean,
  opts?: GaitOpts,
): number {
  const s = state(o);
  s.attackT = 0;
  s.walkBlend = 1;
  s.runBlend = running ? 1 : 0;
  s.phase = (((t % 1) + 1) % 1) * Math.PI * 2;
  o.userData.studioMoving = true;
  o.userData.studioRunning = running;
  o.userData.studioAttack = false;
  // dt=0: blends/phase stay exactly as seeded; the frame is pure pose.
  return animateGait(arch, o, 0, 0, opts);
}

/** Fixed sample times for stride `--t=keys` — quarter-stride footfalls. */
export const GAIT_STRIDE_KEYS = [0, 0.25, 0.5, 0.75] as const;

/** Read-only view of a rig's current gait state (phase + eased blends) — lets a
 *  layered system (the weapon-pose pass, src/anim/pose.ts) ride the walk cycle
 *  without owning it (e.g. the one-hand carry's arm-sway overlay). */
export function gaitSnapshot(o: THREE.Object3D): { phase: number; walkBlend: number; runBlend: number } {
  const s = state(o);
  return { phase: s.phase, walkBlend: s.walkBlend, runBlend: s.runBlend };
}

/** Bind the animator to one archetype → a catalog `animate(o, dt, t)` hook. */
export function makeGait(arch: GaitArchetype) {
  return (o: THREE.Object3D, dt: number, t: number) => animateGait(arch, o, dt, t);
}
